package com.eladfinish.bible.contest.screens.search.usecases

import com.eladfinish.bible.contest.common.usecases.VersePickTarget
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.Regex.FIRST_VERSE
import com.eladfinish.bible.contest.extensions.runIf
import com.eladfinish.bible.contest.screens.search.extensions.withGershaimAndColon
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.utils.gematriaWord
import com.eladfinish.bible.contest.utils.removeWhitespacesAndParshiot
import com.eladfinish.bible.contest.utils.replaceKtivWithQriNew
import com.eladfinish.bible.contest.utils.setNoBreakSpaceBeforePasek
import kotlinx.collections.immutable.persistentListOf
import kotlin.random.Random

// This class is replacing com.eladfinish.bible.contest.common.usecases.PickVerseFromChapterUseCase
class PickVerseFromChapterUseCaseV2(
    private val buildSearchResultSourceUseCase: BuildSearchResultSourceUseCase,
    private val random: Random = Random.Default,
) {

    operator fun invoke(
        bookIndex: Int,
        chapterContent: String,
        target: VersePickTarget,
        onlyQri: Boolean = true,
    ): VerseUiModel? {
        val header = parseHeader(chapterContent)
        val content = chapterContent.substringSafe(
            startIndex = header.contentBeginningIndex,
        )

        val splitter = StringConstants.COLON_SPLITTER
        val verseCount = countVerses(
            content = content,
            splitter = splitter,
        )

        if (verseCount <= 0) {
            return null
        }

        val targetIndex = when (target) {
            VersePickTarget.Random -> {
                random.nextInt(verseCount)
            }

            is VersePickTarget.Nth -> {
                val n = target.verseNumber
                if (n in 1..verseCount) {
                    n - 1
                } else {
                    return null
                }
            }
        }

        val rawVerse = findVerseByIndex(
            content = content,
            splitter = splitter,
            targetIndex = targetIndex,
        ) ?: return null

        val normalized = rawVerse
            .trim()
            .runIf(condition = onlyQri) {
                replaceKtivWithQriNew()
            }
            .removeWhitespacesAndParshiot()

        val (verseName, verseText) = splitVerseNameAndText(normalized) ?: return null

        val cleanVerseText = verseText
            .trim()
            .setNoBreakSpaceBeforePasek()

        return VerseUiModel(
            id = listOf(
                bookIndex.toString(),
                header.chapterName,
                verseName,
            ).joinToString(separator = "_"),
            text = cleanVerseText,
            source = buildSearchResultSourceUseCase(
                bookName = header.bookName,
                bookIndex = bookIndex,
                chapter = header.chapterName,
                verse = verseName,
            ),
            highlightedRanges = persistentListOf(),
            bookIndex = bookIndex,
            chapterIndex = header.chapterName.gematriaWord(),
            verse = verseName,
        ).withGershaimAndColon()
    }

    private data class Header(
        val bookName: String,
        val chapterName: String,
        val contentBeginningIndex: Int,
    )

    private fun parseHeader(
        chapterContent: String,
    ): Header {
        val match = FIRST_VERSE.toRegex().find(chapterContent)
            ?: return Header(
                bookName = "",
                chapterName = "",
                contentBeginningIndex = 0,
            )

        val bookName = match.groupValues.getOrNull(1).orEmpty()
        val chapterName = match.groupValues.getOrNull(2).orEmpty()
        val marker = match.groupValues.getOrNull(3).orEmpty()

        val contentBeginningIndex = when {
            marker.isEmpty() -> 0
            else -> chapterContent.indexOf(marker).takeIf { it >= 0 } ?: 0
        }

        return Header(
            bookName = bookName,
            chapterName = chapterName,
            contentBeginningIndex = contentBeginningIndex,
        )
    }

    private fun countVerses(
        content: String,
        splitter: String,
    ): Int {
        if (content.isBlank()) {
            return 0
        }

        var count = 0
        var startIndex = 0

        while (true) {
            val i = content.indexOf(
                string = splitter,
                startIndex = startIndex,
            )

            if (i < 0) {
                break
            }

            count++
            startIndex = i + splitter.length
        }

        return count
    }

    private fun findVerseByIndex(
        content: String,
        splitter: String,
        targetIndex: Int,
    ): String? {
        if (targetIndex < 0 || content.isBlank()) {
            return null
        }

        var currentIndex = 0
        var start = 0

        while (true) {
            val end = content.indexOf(
                string = splitter,
                startIndex = start,
            )

            if (end < 0) {
                return null
            }

            if (currentIndex == targetIndex) {
                return content.substring(
                    startIndex = start,
                    endIndex = end,
                )
            }

            currentIndex++
            start = end + splitter.length
        }
    }

    private fun splitVerseNameAndText(
        verse: String,
    ): Pair<String, String>? {
        val firstSpace = verse.indexOf(' ')

        if (firstSpace <= 0 || firstSpace >= verse.length - 1) {
            return null
        }

        val verseName = verse.substring(
            startIndex = 0,
            endIndex = firstSpace,
        ).trim()

        val verseText = verse.substring(
            startIndex = firstSpace + 1,
        ).trim()

        if (verseName.isEmpty() || verseText.isEmpty()) {
            return null
        }

        return verseName to verseText
    }

    private fun String.substringSafe(
        startIndex: Int,
    ): String {
        if (startIndex <= 0) {
            return this
        }

        if (startIndex >= length) {
            return ""
        }

        return substring(startIndex)
    }
}
